import { useState, useEffect } from "react";
import { phCapture } from "@/lib/posthog";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Lock, Mail, ArrowLeft } from "lucide-react";

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routerState = window.history.state?.usr as { returnTo?: string } | undefined;
  const returnTo = searchParams.get("returnTo")
    || routerState?.returnTo
    || sessionStorage.getItem("auth_return_to")
    || "/generate";
  const { session, loading: authLoading } = useAuth();

  const [mode, setMode] = useState<"signin" | "signup" | "check-email">(
    searchParams.get("mode") === "signup" ? "signup" : "signin"
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  // Sanitize returnTo: only allow safe paths after auth
  const ALLOWED_PREFIXES = ["/generate", "/processing/", "/report/", "/history", "/pricing"];
  const sanitizedReturnTo = ALLOWED_PREFIXES.some((p) => returnTo.startsWith(p)) ? returnTo : "/generate";

  useEffect(() => {
    if (!authLoading && session) {
      sessionStorage.removeItem("auth_return_to");
      navigate(sanitizedReturnTo);
    }
  }, [authLoading, session, navigate]);

  const handleGoogle = async () => {
    phCapture("sign_in_started", { method: "google" });
    sessionStorage.setItem("auth_return_to", returnTo);
    await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
  };

  const handleResendConfirmation = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({ type: "signup", email: email.trim() });
      if (error) throw error;
      toast.success("Confirmation email resent. Check your inbox.");
    } catch (err: any) {
      toast.error(err.message || "Failed to resend confirmation email.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    phCapture("sign_in_started", { method: "email" });
    setPasswordError("");
    if (password.length < 10) {
      setPasswordError("Please use at least 10 characters.");
      return;
    }
    setLoading(true);

    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) throw error;
        setMode("check-email");
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (error) {
          if (error.message?.toLowerCase().includes("email not confirmed")) {
            setMode("check-email");
            toast.error("Please confirm your email before signing in.");
            return;
          }
          throw error;
        }
        navigate(returnTo);
      }
    } catch (err: any) {
      toast.error(err.message || "Authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      toast.error("Enter your email above first.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success("Password reset email sent. Check your inbox.");
    } catch (err: any) {
      toast.error(err.message || "Failed to send reset email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto flex min-h-[60vh] flex-col items-center justify-center px-4 py-12">
        {/* Trust branding */}
        <div className="mb-6 flex flex-col items-center gap-1">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <Lock className="h-5 w-5 text-teal-400" />
            <span className="gradient-text font-bold">ScopSift</span>
          </div>
          <p className="text-sm dark-muted">Secure sign-in</p>
        </div>

        <Card className="w-full max-w-sm dark-surface border rounded-2xl">
          {mode === "check-email" ? (
            <CardContent className="pt-6 space-y-4 text-center">
              <Mail className="h-10 w-10 text-teal-400 mx-auto" />
              <CardTitle className="text-white text-lg">Check your email</CardTitle>
              <p className="text-sm text-slate-300">
                We've sent a confirmation link to <strong className="text-white">{email}</strong>. Check your inbox (and spam folder).
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full border-[hsl(var(--dark-border))] text-slate-300 hover:text-white"
                onClick={handleResendConfirmation}
                disabled={loading}
              >
                {loading ? "Sending…" : "Resend confirmation email"}
              </Button>
              <button
                type="button"
                onClick={() => setMode("signin")}
                className="inline-flex items-center gap-1 text-sm text-blue-400 hover:underline"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </CardContent>
          ) : (
            <>
              <CardHeader className="text-center">
                <CardTitle className="text-white">{mode === "signin" ? "Sign in" : "Create account"}</CardTitle>
                <CardDescription className="dark-muted">
                  {mode === "signin"
                    ? "Sign in to generate reports."
                    : "Create an account to get started."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-[hsl(var(--dark-border))] dark-muted hover:text-white"
                  onClick={handleGoogle}
                >
                  Continue with Google
                </Button>

                <div className="flex items-center gap-3">
                  <Separator className="flex-1 bg-[hsl(var(--dark-border))]" />
                  <span className="text-xs dark-muted">or</span>
                  <Separator className="flex-1 bg-[hsl(var(--dark-border))]" />
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-slate-200">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="dark-input border"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-slate-200">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                      required
                      minLength={10}
                      className="dark-input border"
                    />
                    {passwordError && <p className="text-xs text-destructive">{passwordError}</p>}
                    <p className="text-xs text-slate-400">Password must be at least 10 characters.</p>
                    {mode === "signup" && (
                      <p className="text-xs text-slate-500">Tip: Use a mix of words, numbers, or symbols.</p>
                    )}
                  </div>

                  <Button type="submit" className="w-full btn-gradient" disabled={loading}>
                    {loading
                      ? "Please wait…"
                      : mode === "signin"
                      ? "Sign in"
                      : "Create account"}
                  </Button>
                </form>

                {mode === "signin" && (
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="block w-full text-center text-xs dark-muted hover:text-white"
                  >
                    Forgot password?
                  </button>
                )}

                <p className="text-center text-sm dark-muted">
                  {mode === "signin" ? (
                    <>
                      Don't have an account?{" "}
                      <button
                        type="button"
                        onClick={() => setMode("signup")}
                        className="font-medium text-blue-400 hover:underline"
                      >
                        Create one
                      </button>
                    </>
                  ) : (
                    <>
                      Already have an account?{" "}
                      <button
                        type="button"
                        onClick={() => setMode("signin")}
                        className="font-medium text-blue-400 hover:underline"
                      >
                        Sign in
                      </button>
                    </>
                  )}
                </p>
              </CardContent>
            </>
          )}
        </Card>

        {/* Trust footer links */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs dark-muted">
          <Link to="/privacy" className="hover:text-white hover:underline">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-white hover:underline">Terms of Service</Link>
          <a href="mailto:dawnrebecca@live.co.uk" className="hover:text-white hover:underline">Contact</a>
        </div>
      </div>
    </Layout>
  );
};

export default Auth;
